import { useCallback } from 'react'

import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { integrationsQueryKeys } from '@/hooks/integrations/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Integration } from '@/types/Integration'

export type UseIntegrationsRefetchInterval = UseQueryOptions<
  PaginatedResponse<Integration>,
  Error,
  PaginatedResponse<Integration>,
  QueryKey
>['refetchInterval']

interface UseIntegrationsArgs<T> {
  orgId: UUID | ''
  pagination?: PaginateRequest
  select?: (data: PaginatedResponse<Integration>) => T
  refetchInterval?: UseIntegrationsRefetchInterval
}

export const fetchIntegrations = async (
  orgId: UUID | '',
  accessToken: string,
  pagination?: PaginateRequest,
): Promise<PaginatedResponse<Integration>> => {
  if (!orgId) throw new Error('No org ID provided.')
  let url = `${API_URL}/api/v1/organizations/${orgId}/integrations`
  if (pagination) {
    const params = new URLSearchParams()
    params.set('limit', String(pagination.limit))
    if (pagination.offset) params.set('offset', String(pagination.offset))
    url += `?${params.toString()}`
  }
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as PaginatedResponse<Integration>
}

export const useIntegrations = <T = Integration[]>({
  orgId,
  pagination,
  select,
  refetchInterval,
}: UseIntegrationsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()
  const selectFn = useCallback(
    (data: PaginatedResponse<Integration>) => {
      if (select) return select(data) as unknown as T
      return data.data as unknown as T
    },
    [select],
  )
  return useQuery({
    queryKey: integrationsQueryKeys.organization({ orgId }),
    queryFn: () => fetchIntegrations(orgId, accessToken, pagination),
    enabled: !!orgId,
    staleTime: Infinity,
    select: selectFn,
    refetchInterval,
  })
}
